import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, Users, Send, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const HourlyCommitmentModal = ({ isOpen, onClose }) => {
  const [hours, setHours] = useState(20);
  const [commitment, setCommitment] = useState('weekly');
  const [scope, setScope] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !scope) return;
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/offers/request`, { client_email: email, hours, commitment, scope });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit request.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => { setSuccess(false); setError(''); setScope(''); }, 300);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
          <motion.div
            className="relative bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col"
            style={{ maxHeight: 'min(90vh, 650px)' }}
            initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            data-testid="commitment-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-brand-blue/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-brand-blue" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900">{success ? 'Request Sent!' : 'Request VA / Custom Offer'}</h3>
                  <p className="text-[11px] text-slate-400">Tell us your requirements</p>
                </div>
              </div>
              <button onClick={handleClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-slate-100 transition-colors" data-testid="commitment-close">
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              {success ? (
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-6">
                  <div className="w-16 h-16 rounded-full bg-green-50 border-4 border-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-500" />
                  </div>
                  <h4 className="text-lg font-bold text-slate-900 mb-2">Request Submitted!</h4>
                  <p className="text-sm text-slate-500">Our team will review your requirements and send you a custom offer within 24 hours.</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-100">
                      <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                      <p className="text-xs text-red-600">{error}</p>
                    </div>
                  )}

                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Your Email</label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="you@company.com"
                      className="w-full px-3.5 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue"
                      data-testid="commitment-email" />
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Hours Required</label>
                    <div className="flex items-center gap-3">
                      <input type="range" min={5} max={160} value={hours} onChange={e => setHours(Number(e.target.value))}
                        className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-brand-blue" data-testid="commitment-hours-slider" />
                      <span className="text-lg font-bold text-brand-blue min-w-[50px] text-right">{hours}h</span>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Commitment</label>
                    <div className="flex gap-2">
                      {['weekly', 'monthly'].map(c => (
                        <button key={c} type="button" onClick={() => setCommitment(c)}
                          className={`flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all ${commitment === c ? 'bg-brand-blue text-white shadow-sm' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                          data-testid={`commitment-${c}`}>
                          <Clock className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />{c === 'weekly' ? 'Weekly' : 'Monthly'}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Project Scope / Tasks</label>
                    <textarea value={scope} onChange={e => setScope(e.target.value)} required rows={4}
                      placeholder="Describe the tasks you need help with..."
                      className="w-full px-3.5 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/20 resize-none"
                      data-testid="commitment-scope" />
                  </div>

                  <button type="submit" disabled={loading || !email || !scope}
                    className="w-full py-3.5 rounded-xl bg-brand-orange text-white text-sm font-bold shadow-lg shadow-brand-orange/20 hover:shadow-xl transition-all disabled:opacity-50 inline-flex items-center justify-center gap-2"
                    data-testid="commitment-submit">
                    {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <><Send className="w-4 h-4" /> Send Request to Admin</>}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
